/**
 * @module Tiptap prompt editor for the Forge composer.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), adapted to Forge's editor
 * contract: the controlled value and cursor tuple, terminal-context chips,
 * the citation provider seam, the prompt typography tokens, and the surface
 * geometry the resting composer measures.
 */

import { EditorContent, type Editor } from "@tiptap/react";
import type { ServerProviderSkill } from "@cmmd-forge/contracts";
import { forwardRef, type ClipboardEventHandler, type Ref } from "react";

import type { TerminalContextDraft } from "~/lib/terminalContext";
import {
  ComposerCitationCommentProvider,
  ComposerCitationInsertContext,
} from "./ComposerCitationNode";
import { RichComposerSkillsContext } from "./composerPromptEditorChips";
import {
  useComposerPromptEditorBinding,
  useComposerTiptapEditor,
} from "./composerPromptEditorBinding";
import { ComposerCitationProvider } from "./ComposerCitationProvider";
import { ComposerPromptPlaceholder } from "./ComposerPromptPlaceholder";

export interface ComposerPromptEditorHandle {
  focus: () => void;
  focusAt: (cursor: number) => void;
  focusAtEnd: () => void;
  readSelectionRange: () => { start: number; end: number };
  readSnapshot: () => {
    value: string;
    cursor: number;
    expandedCursor: number;
    terminalContextIds: string[];
  };
  isCaretOnVisualEdge: (edge: "start" | "end") => boolean;
}

interface ComposerPromptEditorProps {
  value: string;
  cursor: number;
  /**
   * Render markdown styling (bold, italic, code, strike, task checkboxes).
   * Off renders the same Tiptap engine as plain text: every marker stays a
   * literal character.
   */
  richTextEnabled?: boolean;
  terminalContexts: ReadonlyArray<TerminalContextDraft>;
  skills: ReadonlyArray<ServerProviderSkill>;
  disabled: boolean;
  placeholder: string;
  className?: string;
  onRemoveTerminalContext: (contextId: string) => void;
  onChange: (
    nextValue: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
    terminalContextIds: string[],
  ) => void;
  onCommandKeyDown?: (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
    editorValue: string,
  ) => boolean;
  onPaste: ClipboardEventHandler<HTMLElement>;
}

interface ComposerPromptEditorInnerProps extends ComposerPromptEditorProps {
  editorRef: Ref<ComposerPromptEditorHandle>;
  children?: React.ReactNode;
}

function ComposerPromptEditorInner({
  children,
  value,
  cursor,
  richTextEnabled,
  terminalContexts,
  skills,
  disabled,
  placeholder,
  className,
  onRemoveTerminalContext,
  onChange,
  onCommandKeyDown,
  onPaste,
  editorRef,
}: ComposerPromptEditorInnerProps) {
  // The setting toggles styling, not the engine: both modes are Tiptap.
  const richText = richTextEnabled ?? true;
  const { editor, editorHolder, controller, isEmpty } = useComposerTiptapEditor({
    initialValue: value,
    initialCursor: cursor,
    initialTerminalContextIds: terminalContexts.map((context) => context.id),
    onChange,
    onCommandKeyDown,
    onRemoveTerminalContext,
    skills,
    value,
    cursor,
    richText,
    terminalContexts,
    disabled,
    placeholder,
    ...(className !== undefined ? { className } : {}),
  });

  const {
    commentContext,
    editorState: citationEditorState,
    insertCitation,
  } = useComposerPromptEditorBinding({
    editorRef,
    editor,
    controller,
    editorHolder,
    richText,
    value,
    cursor,
    terminalContexts,
    skills,
    disabled,
  });

  const focusEditor = () => {
    if (disabled) return;
    if (editor) controller.focusAt(editor, controller.snapshotRef.current.cursor);
  };

  return (
    <RichComposerSkillsContext value={skills}>
      <ComposerCitationCommentProvider value={commentContext} editorState={citationEditorState}>
        <ComposerCitationInsertContext value={insertCitation}>
          <ComposerPromptEditorView
            editor={editor}
            isEmpty={isEmpty}
            placeholder={placeholder}
            hasTerminalContexts={terminalContexts.length > 0}
            focusEditor={focusEditor}
            onPaste={onPaste}
          />
          {/* The citation provider mounts as a child and registers the insert
              seam, so it has to sit inside the context it reads. */}
          {children}
        </ComposerCitationInsertContext>
      </ComposerCitationCommentProvider>
    </RichComposerSkillsContext>
  );
}

/**
 * The rendered editor surface: the editable wrapper (whose padding is
 * clickable to focus) and the empty-state placeholder.
 */
function ComposerPromptEditorView(props: {
  readonly editor: Editor | null;
  readonly isEmpty: boolean;
  readonly placeholder: string;
  readonly hasTerminalContexts: boolean;
  readonly focusEditor: () => void;
  readonly onPaste: ClipboardEventHandler<HTMLElement>;
}) {
  return (
    <>
      {/* The surface div is EditorContent's own wrapper, so a click on the
          padding focuses the caret while a click in the editable keeps the
          native caret placement. */}
      <EditorContent
        editor={props.editor}
        className="relative min-h-17.5 cursor-text"
        onMouseDown={(event) => {
          if (event.target !== event.currentTarget) return;
          event.preventDefault();
          props.focusEditor();
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          props.focusEditor();
        }}
        onPasteCapture={props.onPaste}
      />
      {props.isEmpty && !props.hasTerminalContexts && props.placeholder ? (
        <ComposerPromptPlaceholder text={props.placeholder} />
      ) : null}
    </>
  );
}

export const ComposerPromptEditorTiptap = forwardRef<
  ComposerPromptEditorHandle,
  ComposerPromptEditorProps
>(function ComposerPromptEditorTiptap({ richTextEnabled, ...props }, ref) {
  // Extensions are creation-time: flipping the setting remounts the editor.
  // Both halves initialize from the controlled prompt, so the draft survives
  // the flip.
  return (
    <ComposerPromptEditorInner
      key={richTextEnabled === false ? "plain" : "rich"}
      {...(richTextEnabled !== undefined ? { richTextEnabled } : {})}
      editorRef={ref}
      {...props}
    >
      <ComposerCitationProvider />
    </ComposerPromptEditorInner>
  );
});
